import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { FilesList } from "../components/SecureFile";
import { useAuth } from "../lib/auth";
import { ArrowLeft, UserCheck, Clock, Check, Calendar, Plus, Trash2, BookMarked, Users, LayoutList, Layers } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ATTENDANCE_META } from "../lib/attendance";
import { RattrapagesBulletin } from "../components/RattrapagesBulletin";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function nextWednesday() {
  const d = new Date();
  const diff = (3 - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function RattList({ rattrapages, groupByClass, groupedRatt, busy, markRatt }) {
  if (groupByClass) {
    return Object.entries(groupedRatt).map(([className, items]) => (
      <div key={className} className="mt-5" data-testid={`ratt-class-group-${className}`}>
        <div className="eyebrow !text-[var(--maroon)] mb-2">{className} · {items.length} élève{items.length > 1 ? "s" : ""}</div>
        <div className="space-y-3">
          {items.map((r) => (
            <RattCard key={r.id} r={r} busy={busy === r.id} onMark={(s) => markRatt(r.id, s)} />
          ))}
        </div>
      </div>
    ));
  }
  return (
    <div className="mt-4 space-y-3">
      {rattrapages.map((r) => (
        <RattCard key={r.id} r={r} busy={busy === r.id} onMark={(s) => markRatt(r.id, s)} showClass />
      ))}
    </div>
  );
}

function useEtudeActions(load, date) {
  const [busy, setBusy] = useState(null);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const markRatt = (id, status) => run(id, () => api.post(`/rattrapages/${id}/attendance`, { status }));
  const markEtude = (id, status) => run(id, () => api.post(`/etude/entries/${id}/attendance`, { status }));
  const removeEntry = (id) => run(null, () => api.delete(`/etude/entries/${id}`));
  const validateSheet = () => run("validate", () => api.post("/etude/validate", { date }), "Feuille d'appel de l'étude validée");

  return { busy, markRatt, markEtude, removeEntry, validateSheet };
}

export default function Etude() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(nextWednesday());
  const [groupByClass, setGroupByClass] = useState(true);
  const [registerDialog, setRegisterDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/etude/day", { params: { date } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const { busy, markRatt, markEtude, removeEntry, validateSheet } = useEtudeActions(load, date);

  const rattrapages = data?.rattrapages || [];
  const entries = data?.etude_entries || [];
  const backLink = user?.role === "direction" ? "/admin" : "/teacher";

  const groupedRatt = rattrapages.reduce((acc, r) => {
    const key = r.class?.name || "—";
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to={backLink} className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="etude-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-6 pb-8 border-b border-[var(--parchment-2)]">
          <div>
            <div className="eyebrow">Étude du mercredi matin</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Appel de l'étude</h1>
            <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
              Deux espaces distincts : les élèves convoqués en <strong>rattrapage</strong> (avec les documents
              à imprimer ou diffuser) et les élèves inscrits à l'<strong>étude simple</strong>.
              Les convoqués sont <strong>présents par défaut</strong> — ne cochez que les absents, puis validez la feuille.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1">
              <ViewBtn active={groupByClass} onClick={() => setGroupByClass(true)} icon={<Layers size={14} />} label="Par classe" testId="view-by-class" />
              <ViewBtn active={!groupByClass} onClick={() => setGroupByClass(false)} icon={<LayoutList size={14} />} label="Vue globale" testId="view-global" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <span className="eyebrow">Date</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none font-mono-spec text-sm"
                data-testid="etude-date-input"
              />
            </label>
            <EtudeValidationControl validation={data?.validation} busy={busy} loading={loading} onValidate={validateSheet} />
          </div>
        </div>

        <RattrapagesBulletin />

        {loading ? (
          <div className="mt-10 text-[var(--ink-soft)]">Chargement...</div>
        ) : (
          <div className="mt-8 grid xl:grid-cols-2 gap-8 items-start">
            <RattrapageSection rattrapages={rattrapages} groupByClass={groupByClass} groupedRatt={groupedRatt} busy={busy} markRatt={markRatt} />
            <EtudeSimpleSection entries={entries} busy={busy} markEtude={markEtude} removeEntry={removeEntry} onRegister={() => setRegisterDialog(true)} />
          </div>
        )}
      </main>

      <RegisterDialog open={registerDialog} onClose={() => setRegisterDialog(false)} date={date} onDone={load} />
    </div>
  );
}

function EtudeValidationControl({ validation, busy, loading, onValidate }) {
  if (validation) {
    return (
      <div className="flex items-center gap-2 text-xs bg-[var(--ok)]/10 text-[var(--ok)] border border-[var(--ok)]/30 px-3 py-2" data-testid="etude-validated-badge">
        <Check size={14} />
        <span>
          Feuille validée par {validation.validated_by_name} le{" "}
          {new Date(validation.validated_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
        </span>
        <button className="underline hover:text-[var(--maroon)]" onClick={onValidate} disabled={busy === "validate"} data-testid="etude-revalidate-btn">
          Revalider
        </button>
      </div>
    );
  }
  return (
    <button
      className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2 disabled:opacity-50"
      onClick={onValidate}
      disabled={busy === "validate" || loading}
      data-testid="etude-validate-btn"
    >
      <Check size={15} /> {busy === "validate" ? "Validation..." : "Valider la feuille d'appel"}
    </button>
  );
}

function EtudeSimpleSection({ entries, busy, markEtude, removeEntry, onRegister }) {
  return (
    <section className="bg-white border border-[var(--parchment-2)] p-6" data-testid="etude-simple-space">
      <div className="flex items-center gap-2 pb-3 border-b border-[var(--parchment-2)]">
        <Users size={18} className="text-[var(--maroon)]" />
        <h2 className="font-serif-display text-2xl font-semibold">Étude simple</h2>
        <span className="eyebrow">{entries.length}</span>
        <button className="btn-primary !py-1.5 !px-3 text-xs ml-auto flex items-center gap-1.5" onClick={onRegister} data-testid="register-etude-btn">
          <Plus size={13} /> Inscrire des élèves
        </button>
      </div>
      {entries.length === 0 ? (
        <div className="mt-4 text-sm text-[var(--ink-soft)] italic">Aucun élève inscrit à l'étude simple à cette date.</div>
      ) : (
        <div className="mt-4 divide-y divide-[var(--parchment-2)]">
          {entries.map((e) => (
            <div key={e.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`etude-entry-${e.id}`}>
              <div>
                <span className="font-medium text-sm">{e.student?.last_name.toUpperCase()} {e.student?.first_name}</span>
                <span className="text-xs text-[var(--ink-soft)] ml-2">{e.class?.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <ActionBtn active={e.attendance_status === "present"} color="var(--ok)" icon={<UserCheck size={13} />} label="Présent" disabled={busy === e.id} onClick={() => markEtude(e.id, "present")} testId={`etude-entry-present-${e.id}`} />
                <ActionBtn active={e.attendance_status === "late"} color="var(--just)" icon={<Clock size={13} />} label="Retard" disabled={busy === e.id} onClick={() => markEtude(e.id, "late")} testId={`etude-entry-late-${e.id}`} />
                <ActionBtn active={e.attendance_status === "absent"} color="var(--late)" icon={<Check size={13} />} label="Absent" disabled={busy === e.id} onClick={() => markEtude(e.id, "absent")} testId={`etude-entry-absent-${e.id}`} />
                <button className="text-[var(--muted)] hover:text-[var(--late)] ml-1" onClick={() => removeEntry(e.id)} data-testid={`etude-entry-delete-${e.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function RattrapageSection({ rattrapages, groupByClass, groupedRatt, busy, markRatt }) {
  return (
    <section className="bg-white border border-[var(--parchment-2)] p-6" data-testid="etude-rattrapage-space">
      <div className="flex items-center gap-2 pb-3 border-b border-[var(--parchment-2)]">
        <BookMarked size={18} className="text-[var(--maroon)]" />
        <h2 className="font-serif-display text-2xl font-semibold">Rattrapages convoqués</h2>
        <span className="eyebrow ml-auto">{rattrapages.length}</span>
      </div>
      {rattrapages.length === 0 ? (
        <div className="mt-4 text-sm text-[var(--ink-soft)] italic">Aucun rattrapage prévu à cette date.</div>
      ) : (
        <RattList rattrapages={rattrapages} groupByClass={groupByClass} groupedRatt={groupedRatt} busy={busy} markRatt={markRatt} />
      )}
    </section>
  );
}

function RegisterDialog({ open, onClose, date, onDone }) {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/classes").then(({ data }) => {
      setClasses(data);
      setClassId(data[0]?.id || "");
    });
    setSelected({});
  }, [open]);

  useEffect(() => {
    if (!classId) return;
    api.get(`/classes/${classId}/students`).then(({ data }) => setStudents(data));
    setSelected({});
  }, [classId]);

  const submit = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) {
      toast.error("Cochez au moins un élève");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/etude/register", { date, class_id: classId, student_ids: ids });
      toast.success(`${data.added} élève${data.added > 1 ? "s" : ""} inscrit${data.added > 1 ? "s" : ""} à l'étude`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Inscrire à l'étude simple · {date}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="eyebrow block mb-1">Classe</label>
            <select className={inputCls} value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="etude-register-class-select">
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-1">Élèves</label>
            <div className="max-h-56 overflow-y-auto border border-[var(--parchment-2)] divide-y divide-[var(--parchment-2)]">
              {students.map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[var(--parchment)]/50">
                  <input
                    type="checkbox"
                    checked={!!selected[s.id]}
                    onChange={() => setSelected((sel) => ({ ...sel, [s.id]: !sel[s.id] }))}
                    className="accent-[var(--maroon)] w-4 h-4"
                    data-testid={`etude-register-check-${s.id}`}
                  />
                  <span>{s.last_name.toUpperCase()} {s.first_name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={submit} disabled={busy} data-testid="etude-register-submit">Inscrire</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RattCard({ r, busy, onMark, showClass }) {
  return (
    <div className="border border-[var(--parchment-2)] p-4" data-testid={`etude-card-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-serif-display text-lg font-semibold">
          {r.student?.last_name.toUpperCase()} {r.student?.first_name}
        </span>
        {showClass && (
          <>
            <span className="text-[var(--muted)]">·</span>
            <span className="text-sm text-[var(--ink-soft)]">{r.class?.name}</span>
          </>
        )}
        <span className="text-[var(--muted)]">·</span>
        <span className="text-sm text-[var(--maroon)] font-medium">{r.subject?.name}</span>
        {r.attendance_status && (
          <span
            className="px-2 py-0.5 text-white text-xs font-medium"
            style={{ background: ATTENDANCE_META[r.attendance_status].color }}
          >
            {ATTENDANCE_META[r.attendance_status].label}
          </span>
        )}
      </div>
      <div className="mt-1 text-xs text-[var(--muted)] flex items-center gap-3">
        <span className="flex items-center gap-1"><Calendar size={12} /> {r.scheduled_date}</span>
        <span>Assigné par {r.assigned_by?.name}</span>
        {r.supervised_by && <span>· Noté par {r.supervised_by.name}</span>}
      </div>
      <div className="mt-2 text-sm text-[var(--ink)] whitespace-pre-line">{r.description}</div>
      <FilesList files={r.files} photoPath={r.photo_path} />
      <div className="mt-3 flex gap-2">
        <ActionBtn active={r.attendance_status === "present"} color="var(--ok)" icon={<UserCheck size={13} />} label="Présent" disabled={busy} onClick={() => onMark("present")} testId={`etude-mark-present-${r.id}`} />
        <ActionBtn active={r.attendance_status === "late"} color="var(--just)" icon={<Clock size={13} />} label="Retard" disabled={busy} onClick={() => onMark("late")} testId={`etude-mark-late-${r.id}`} />
        <ActionBtn active={r.attendance_status === "absent"} color="var(--late)" icon={<Check size={13} />} label="Absent" disabled={busy} onClick={() => onMark("absent")} testId={`etude-mark-absent-${r.id}`} />
      </div>
    </div>
  );
}

function ViewBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border transition ${
        active ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function ActionBtn({ active, color, icon, label, disabled, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition ${
        active ? "text-white" : "text-[var(--ink-soft)] bg-white border-[var(--parchment-2)] hover:border-[var(--ink)]"
      } ${disabled ? "opacity-60" : "cursor-pointer"}`}
      style={active ? { background: color, borderColor: color } : {}}
    >
      {icon} {label}
    </button>
  );
}
